import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CalendarDays, Users, FileText, CheckCircle2 } from "lucide-react";
import { createAppointment, getClients } from "@/utils/api";
import { subscribePushReminder, requestPermission } from "@/utils/notifications";
import { toast } from "sonner";
import { format, parseISO, addDays, getISODay, setHours, setMinutes, subDays } from "date-fns";
import { it } from "date-fns/locale";

const SELECT_CLASS = "mt-1 h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer";

const DAY_LABEL_FULL = {
  0: "Lunedì", 1: "Martedì", 2: "Mercoledì",
  3: "Giovedì", 4: "Venerdì", 5: "Sabato", 6: "Domenica"
};

function nextPreferredDay(preferredDay, fromDateStr) {
  const from = fromDateStr ? parseISO(fromDateStr) : new Date();
  const targetISODay = preferredDay + 1;
  const currentISODay = getISODay(from);
  let daysAhead = targetISODay - currentISODay;
  if (daysAhead < 0) daysAhead += 7;
  return format(addDays(from, daysAhead), "yyyy-MM-dd");
}

export default function AppointmentScheduler({ open, onClose, onSuccess, preselectedClientId, preselectedDate }) {
  const [mode, setMode] = useState("client"); // "client" | "free"
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(preselectedClientId || "");
  const [freeTitle, setFreeTitle] = useState("");
  const [date, setDate] = useState(preselectedDate || new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestedDay, setSuggestedDay] = useState(null);

  // Reminder step after creation
  const [createdApt, setCreatedApt] = useState(null);
  const [createdAptName, setCreatedAptName] = useState("");
  const [morningTime, setMorningTime] = useState("08:00");
  const [eveningTime, setEveningTime] = useState("20:00");
  const [reminderLoading, setReminderLoading] = useState(false);

  useEffect(() => {
    getClients().then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      setMode(preselectedClientId ? "client" : "client");
      setFreeTitle("");
      setNotes("");
      setSuggestedDay(null);
      setCreatedApt(null);
      setCreatedAptName("");
    }
    if (preselectedClientId && clients.length > 0) {
      setClientId(preselectedClientId);
      const client = clients.find((c) => c.id === preselectedClientId);
      if (client && client.preferred_day != null && !preselectedDate) {
        const suggested = nextPreferredDay(client.preferred_day);
        setDate(suggested);
        setSuggestedDay(DAY_LABEL_FULL[client.preferred_day]);
      }
    }
    if (preselectedDate) setDate(preselectedDate);
  }, [preselectedClientId, preselectedDate, open, clients]);

  const handleClientChange = (e) => {
    const id = e.target.value;
    setClientId(id);
    const client = clients.find((c) => c.id === id);
    if (client && client.preferred_day != null) {
      const suggested = nextPreferredDay(client.preferred_day, preselectedDate || undefined);
      setDate(suggested);
      setSuggestedDay(DAY_LABEL_FULL[client.preferred_day]);
    } else {
      setSuggestedDay(null);
      if (!preselectedDate) setDate(new Date().toISOString().split("T")[0]);
    }
  };

  const handleClose = () => {
    setNotes("");
    setFreeTitle("");
    setSuggestedDay(null);
    setCreatedApt(null);
    setCreatedAptName("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "client" && !clientId) { toast.error("Seleziona un cliente"); return; }
    if (mode === "free" && !freeTitle.trim()) { toast.error("Inserisci un titolo per l'appuntamento"); return; }
    if (!date) { toast.error("Seleziona una data"); return; }
    setLoading(true);
    try {
      const payload = mode === "client"
        ? { client_id: clientId, scheduled_date: date, scheduled_time: time || null, notes }
        : { title: freeTitle.trim(), scheduled_date: date, scheduled_time: time || null, notes };
      const res = await createAppointment(payload);
      toast.success("Appuntamento aggiunto");
      onSuccess?.();
      const name = mode === "client"
        ? (clients.find(c => c.id === clientId)?.company || clients.find(c => c.id === clientId)?.name || "Cliente")
        : freeTitle.trim();
      setCreatedAptName(name);
      setCreatedApt(res.data);
    } catch {
      toast.error("Errore nella creazione dell'appuntamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSetReminder = async (option) => {
    setReminderLoading(true);
    const granted = await requestPermission();
    if (!granted) {
      toast.error("Abilita le notifiche nelle impostazioni del browser");
      setReminderLoading(false);
      return;
    }
    const [h, m] = (option === "morning" ? morningTime : eveningTime).split(":").map(Number);
    const apptDay = parseISO(createdApt.scheduled_date);
    const reminderTime = option === "morning"
      ? setMinutes(setHours(apptDay, h), m)
      : setMinutes(setHours(subDays(apptDay, 1), h), m);

    if (reminderTime < new Date()) {
      toast.error("Orario già passato, scegli un orario futuro");
      setReminderLoading(false);
      return;
    }
    await subscribePushReminder(createdApt.id, reminderTime, createdAptName);
    const when = option === "morning" ? `Mattina (${morningTime})` : `Sera prima (${eveningTime})`;
    toast.success(`Promemoria impostato: ${when}`, {
      description: format(reminderTime, "EEEE d MMMM 'alle' HH:mm", { locale: it }),
    });
    setReminderLoading(false);
    handleClose();
  };

  // --- Reminder step (shown after successful creation) ---
  if (createdApt) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="appointment-scheduler-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
              Promemoria
            </DialogTitle>
          </DialogHeader>

          <div className="text-center py-1">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
            </div>
            <p className="font-semibold text-[#0F172A]">Appuntamento creato!</p>
            <p className="text-sm text-slate-500 mt-1">
              {createdAptName} · {format(parseISO(createdApt.scheduled_date), "dd/MM/yyyy")}
            </p>
          </div>

          <p className="text-sm text-slate-600 text-center">Vuoi impostare un promemoria?</p>

          <div className="space-y-2">
            {/* Mattina */}
            <div className="border border-slate-100 rounded-xl p-3">
              <div className="text-xs font-semibold text-slate-700 mb-2">Mattina dell'appuntamento</div>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={morningTime}
                  onChange={(e) => setMorningTime(e.target.value)}
                  className="flex-1 h-8 rounded-lg border border-slate-200 px-2 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  data-testid="reminder-morning-time"
                />
                <Button
                  onClick={() => handleSetReminder("morning")}
                  disabled={reminderLoading}
                  size="sm"
                  className="bg-[#0F172A] text-white text-xs"
                  data-testid="reminder-morning-btn"
                >
                  Imposta
                </Button>
              </div>
            </div>

            {/* Sera prima */}
            <div className="border border-slate-100 rounded-xl p-3">
              <div className="text-xs font-semibold text-slate-700 mb-2">La sera prima</div>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={eveningTime}
                  onChange={(e) => setEveningTime(e.target.value)}
                  className="flex-1 h-8 rounded-lg border border-slate-200 px-2 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  data-testid="reminder-evening-time"
                />
                <Button
                  onClick={() => handleSetReminder("evening_before")}
                  disabled={reminderLoading}
                  size="sm"
                  className="bg-[#0F172A] text-white text-xs"
                  data-testid="reminder-evening-btn"
                >
                  Imposta
                </Button>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full"
            data-testid="reminder-skip-btn"
          >
            Salta
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Normal creation form ---
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="appointment-scheduler-modal" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
            Nuovo Appuntamento
          </DialogTitle>
        </DialogHeader>

        {/* Toggle modo */}
        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
          <button
            type="button"
            data-testid="mode-client"
            onClick={() => setMode("client")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
              mode === "client" ? "bg-[#0F172A] text-white" : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            Cliente
          </button>
          <button
            type="button"
            data-testid="mode-free"
            onClick={() => setMode("free")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
              mode === "free" ? "bg-[#0F172A] text-white" : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Occasionale
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "client" ? (
            <div>
              <Label htmlFor="apt-client-select">Cliente</Label>
              <select
                id="apt-client-select"
                data-testid="apt-client-select"
                value={clientId}
                onChange={handleClientChange}
                className={SELECT_CLASS}
              >
                <option value="">— Seleziona cliente —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company || c.name}{c.company && c.name ? ` · ${c.name}` : ""}{c.preferred_day != null ? ` (${DAY_LABEL_FULL[c.preferred_day]})` : ""}
                  </option>
                ))}
              </select>
              {suggestedDay && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  Data suggerita: prossimo {suggestedDay}
                </p>
              )}
            </div>
          ) : (
            <div>
              <Label htmlFor="apt-free-title">Descrizione appuntamento</Label>
              <Input
                id="apt-free-title"
                data-testid="apt-free-title"
                placeholder="Es: Riunione con fornitore, Fiera di Milano..."
                value={freeTitle}
                onChange={(e) => setFreeTitle(e.target.value)}
                className="mt-1"
                autoFocus
              />
            </div>
          )}

          <div>
            <Label>Data e Ora</Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="apt-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="flex-1"
                required
              />
              <Input
                data-testid="apt-time-input"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-28"
              />
            </div>
            {date && (
              <p className="text-xs text-slate-400 mt-1">
                {format(parseISO(date), "EEEE d MMMM yyyy", { locale: it })}
                {time ? ` alle ${time}` : ""}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="apt-notes">Note (opzionale)</Label>
            <Textarea
              id="apt-notes"
              data-testid="apt-notes-input"
              placeholder="Note sull'appuntamento..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 resize-none"
            />
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1" data-testid="apt-cancel-btn">
              Annulla
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#0F172A] text-white" data-testid="apt-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
